import React, { Component } from 'react';
import { Container, Row, Col, Card, Badge, Alert, Nav } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import { GoRepo, GoHistory, GoGear, GoChevronRight } from 'react-icons/go';
import BlobHeader from "./header";
import BlobRevisions from "./revisions";
import BlobMarkdown from "./md";
import BlobSettings from "./settings/settings";
import BlobTabs from "components/blob/BlobTabs/index";
import BlobHeaderToggle from "components/blob/BlobHeaderToggle/index";
import ShortcutsModal from "components/blob/shortcuts/index";
import { HeaderSlotPortal } from "components/controls/HeaderSlot/index";
import ConfirmModal from "components/controls/confirmModal";
import Navigation from "context/nav";
import Preferences from "context/preferences";
import Title from "context/title";
import Utils from "context/utils";
import { isTypingTarget } from "context/keyboard";
import {
  applyRevisionMemory, arrivalCandidates, memoryOnFailure, memoryOnLoad, rememberedRevision, revisionFetchFailure
} from "./revisionResolution";
import createUnsavedWorkRegistry from "./unsavedWork";
import './index.css';

import { SHELLS } from 'blob_types/registry';

// blobhub.graph.orientdb is the one type with a second top-level pane (Publish), alongside the
// Shell every type gets from the registry.
import GraphOrientdbPublish from 'blob_types/blobhub/graph/orientdb/publish/index';

// Where the page went instead, keyed by the `from` of the revision that loaded, or "stayed".
const STALE_REVISION_LANDINGS = {
  default: "This blob is showing its default revision instead.",
  saved: "This blob is showing the revision you chose last instead.",
  stayed: "The revision on screen has not changed.",
};

export default class Blob extends Component {
  constructor(props) {
    super(props);

    this.state = {
      notFound: false,
      org: null,
      blob: null,
      revision: null,
      // The revisions the page could not show and where it went instead, until the notice is dismissed:
      // { failures: [{ id, reason, kept }], landing }, with `reason` from revisionFetchFailure.
      staleRevision: null,
      // The selector's list, fetched when its menu first opens, and its rows' authors (user id -> user).
      // Null means "not fetched", never "empty". It labels the menu and decides nothing.
      revisions: null,
      users: {},
      // A change waiting on the unsaved-work question: { candidates, failures, surfaces }, in one field so
      // the question and what it is about arrive and leave together.
      pendingSwitch: null,
      shortcutsShow: false,
      loadBlob: this.loadBlob,
      loadActiveRevision: this.loadActiveRevision
    };

    // Guards title writes from late-arriving promise callbacks (see componentWillUnmount).
    this.unmounted = false;

    // Whether the list has been asked for. An instance field rather than state, because a second
    // menu open in the same tick must see the first request, and a setState has not landed by then.
    this.revisionsRequested = false;

    // The revision request the page is waiting on. An answer to any other is dropped unread.
    this.revisionRequest = null;

    // The search the page has just written to the bar and not yet seen come back. Its own correction
    // reaches componentDidUpdate looking exactly like a reader's move, and only this tells them apart.
    this.barWrite = null;

    // Which answer closed the switch dialog. ConfirmModal calls onConfirm and onHide in that order,
    // so the close has to be told; a request in flight says nothing about who started it.
    this.switchConfirmed = false;

    // Built once, not per render: the surfaces below register on mount and unregister on unmount, so
    // a fresh object each render would strand every registration made against the previous one.
    this.unsavedWork = createUnsavedWorkRegistry();
  }

  componentDidMount() {
    this.syncTitle();

    this.loadOrg();

    this.unsubscribePrefs = Preferences.subscribe(() => this.forceUpdate());
    document.addEventListener("keydown", this.handleGlobalKeyDown);
  }

  // A change of `?revision=` the page did not make -- Back, Forward, a pasted link -- opens a different
  // view of this blob, so it arrives again. Scoped strictly to the query: a reload is not an arrival.
  // docs/specs/2026-09-18-revision-selection.md#arrival-refresh-and-the-one-door
  componentDidUpdate(prevProps) {
    if ( this.props.match.search === prevProps.match.search ) {
      return;
    }
    if ( this.ownBarWrite() ) {
      return;
    }
    this.reconcileRevisionQuery();
  }

  // Whether this change of the bar is the echo of the page's own correction. Held for one change
  // only: what the page did not write is the reader's, and arrival is theirs to open.
  ownBarWrite() {
    const written = this.barWrite;
    this.barWrite = null;
    return null !== written && written === (this.props.match.search || "");
  }

  componentWillUnmount() {
    this.unmounted = true;

    if (this.unsubscribePrefs) this.unsubscribePrefs();
    document.removeEventListener("keydown", this.handleGlobalKeyDown);
  }

  // Blob-level keyboard shortcuts. Ignored on auto-repeat, while typing in a
  // field (⌘/Ctrl+E overlaps macOS "end of line" on Ctrl; "?" is a literal
  // character), and while any modal is open (so we never toggle the header or
  // open the popup behind a dialog). Tab-specific shortcuts (⌘S / ⌘R / Delete /
  // Esc) live in the workflow editor/playground handlers.
  handleGlobalKeyDown = (event) => {
    if (event.repeat) return;
    if (isTypingTarget(event.target)) return;
    if (document.body.classList.contains("modal-open")) return;

    // ⌘/Ctrl + E — toggle the blob header (expand / collapse).
    if ((event.metaKey || event.ctrlKey) && !event.shiftKey && !event.altKey
        && (event.key === "e" || event.key === "E")) {
      event.preventDefault();
      this.toggleHeaderCollapsed();
      return;
    }

    // ? — open the keyboard-shortcuts help popup. Match the resulting character
    // (so it works however "?" is produced, e.g. Shift+/ or AltGr) as long as
    // no Command chord is held.
    if (event.key === "?" && !event.metaKey) {
      event.preventDefault();
      this.setState({ shortcutsShow: true });
    }
  };

  toggleHeaderCollapsed() {
    if (!this.state.org || !this.state.blob) return;
    const scope = this.scope();
    const current = !!Preferences.get("app.blobHeaderCollapsed", scope);
    Preferences.set("app.blobHeaderCollapsed", !current, scope);
  }

  /**
   * Both aliases come from the route params, so this is correct from first paint and is simply
   * re-derived with better data as loadOrg and loadBlob resolve.
   */
  syncTitle() {
    // A setState callback that resolves after the user has already navigated away (e.g. via the
    // footer's <Link to="/terms">) would otherwise overwrite whatever page they landed on next.
    if (this.unmounted) return;

    const { orgId, blobId } = this.props.match.params;
    Title.set(Utils.displayName(this.state.org, orgId), Utils.displayName(this.state.blob, blobId));
  }

  loadOrg() {
    const orgId = this.props.match.params.orgId;

    this.props.app.api.orgsIdGet(orgId)
      .then(response => {
        this.setState({
          org: response.data.org
        }, () => {
          this.syncTitle();
          this.loadBlob();
        });
      }, error => {
        // Org may be inaccessible when the user has direct blob membership but not org membership.
        // Proceed to load the blob; if that also fails, notFound is set there.
        this.loadBlob();
      });
  }

  loadBlob = () => {
    const orgId = this.props.match.params.orgId;
    const blobId = this.props.match.params.blobId;

    this.props.app.api.blobsIdGet(orgId, blobId)
      .then(response => {
        const blob = response.data.blob;
        const update = { blob };
        if (!this.state.org) {
          update.org = { id: blob.org_id, alias: orgId, name: null };
        }
        this.setState(update, () => {
          this.syncTitle();
          this.finishLoadingBlob();
        });
      }, error => {
        this.setState({
          notFound: true
        });
        if (!this.unmounted) Title.setNotFound();
      });
  }

  finishLoadingBlob() {
    if ( this.props.app.api.hasBlobReachedTerminalSuccessStatus(this.state.blob) ) {
      this.loadActiveRevision();
    } else if ( this.props.app.api.isBlobInUpdatingStatus(this.state.blob) ) {
      this.loadActiveRevision();
      setTimeout(() => {
        this.loadBlob();
      }, 3000);
    } else if ( this.props.app.api.hasBlobReachedTerminalFailureStatus(this.state.blob) ) {
      // Nothing for now
    } else if ( this.props.app.api.isBlobInDeletingStatus(this.state.blob) ) {
      Navigation.push(this.props.history, Navigation.orgPath(this.state.org));
    } else {
      setTimeout(() => {
        this.loadBlob();
      }, 3000);
    }
  }

  // Arrival chooses which revision is shown, and lasts until one is; every later reload only re-reads
  // it (docs/specs/2026-09-18-revision-selection.md#arrival-refresh-and-the-one-door).
  loadActiveRevision = () => {
    if ( !this.state.blob || this.revisionRequest ) {
      return;
    }
    if ( this.state.revision ) {
      this.refreshRevision();
    } else {
      this.arrive();
    }
  }

  arrive() {
    this.changeRevision(this.candidatesFor(this.translateLegacyRevisionPath() || this.queryRevisionId()));
  }

  candidatesFor(claimedId) {
    return arrivalCandidates({
      claimedId,
      storedId: rememberedRevision(this.scope()),
      defaultRevisionId: this.state.blob["default_revision_id"]
    });
  }

  // Either the bar or the screen can be the one that moved: a bar naming another revision is an
  // arrival, a bar behind the screen is corrected in place, and a fetch the reader has navigated away
  // from is abandoned whichever of the two it is.
  reconcileRevisionQuery() {
    if ( !this.state.blob ) {
      return;
    }
    const candidates = this.candidatesFor(this.queryRevisionId());
    const wantedId = candidates.length ? candidates[0].id : null;
    const abandoned = this.abandonUnwantedChange(wantedId);
    if ( !this.state.revision ) {
      // With nothing on screen, arrival owns the page: only the fetch it is waiting on can be out of
      // date, and arriving again on what the bar now claims is what replaces it.
      if ( abandoned ) {
        this.arrive();
      }
      return;
    }
    const shownId = this.state.revision.id;
    if ( !wantedId || wantedId === shownId ) {
      this.syncRevisionQuery(shownId, this.state.blob["default_revision_id"]);
      return;
    }
    this.changeRevision(candidates);
  }

  // `settle` asks which request is newest; this asks whether the reader still wants the change at
  // all. A change heading for a revision that is neither on screen nor claimed by the bar has been
  // navigated away from, whether it is a fetch on its way or a question waiting for an answer.
  abandonUnwantedChange(wantedId) {
    const shownId = this.state.revision && this.state.revision.id;
    const unwanted = id => id !== wantedId && id !== shownId;
    const pending = this.state.pendingSwitch;
    if ( pending && unwanted(pending.candidates[0].id) ) {
      this.setState({pendingSwitch: null});
    }
    const request = this.revisionRequest;
    if ( !request || !unwanted(request.id) ) {
      return false;
    }
    this.revisionRequest = null;
    return true;
  }

  // Keeps the revision on screen current. Only a 404 moves the page, and then through the door.
  refreshRevision() {
    const shownId = this.state.revision.id;
    const request = this.requestRevision(shownId);
    request.answer.then(response => {
      if ( this.settle(request) ) {
        const revision = this.loadedRevision(response);
        if ( revision ) {
          this.setState({revision});
        }
      }
    }, error => {
      if ( this.settle(request) && "gone" === revisionFetchFailure(error) ) {
        this.leaveGoneRevision(shownId);
      }
    });
  }

  leaveGoneRevision(goneId) {
    const scope = this.scope();
    applyRevisionMemory(scope, {remember: memoryOnFailure(goneId, "gone", rememberedRevision(scope))});
    const defaultRevisionId = this.state.blob["default_revision_id"];
    const failures = [{id: goneId, reason: "gone", kept: false}];
    if ( !defaultRevisionId || defaultRevisionId === goneId ) {
      this.setState({staleRevision: {failures, landing: null}});
      return;
    }
    this.changeRevision([{id: defaultRevisionId, from: "default"}], failures);
  }

  // The one door for every change to the revision on screen: asks first when the change would lose unsaved
  // work, then tries `candidates` in order until one loads. `failures` are what led here.
  changeRevision(candidates, failures = []) {
    const surfaces = this.state.revision ? this.unsavedWork.pending() : [];
    if ( candidates.length && surfaces.length ) {
      this.setState({pendingSwitch: {candidates, failures, surfaces}});
      return;
    }
    this.tryRevisions(candidates, failures);
  }

  tryRevisions([candidate, ...rest], failures) {
    if ( !candidate ) {
      if ( failures.length ) {
        this.setState({staleRevision: {failures, landing: this.state.revision ? "stayed" : null}});
      }
      return;
    }
    const request = this.requestRevision(candidate.id);
    request.answer.then(response => {
      if ( !this.settle(request) ) {
        return;
      }
      const revision = this.loadedRevision(response);
      if ( revision ) {
        this.showRevision(revision, candidate, failures);
      } else {
        this.revisionFailed(candidate, this.unusableAnswer(response), rest, failures);
      }
    }, error => {
      if ( this.settle(request) ) {
        this.revisionFailed(candidate, revisionFetchFailure(error), rest, failures);
      }
    });
  }

  revisionFailed(candidate, reason, rest, failures) {
    const scope = this.scope();
    const storedId = rememberedRevision(scope);
    applyRevisionMemory(scope, {remember: memoryOnFailure(candidate.id, reason, storedId)});
    const kept = "transient" === reason && candidate.id === storedId;
    this.tryRevisions(rest, [...failures, {id: candidate.id, reason, kept}]);
  }

  // The address bar is written only here, so a copied URL names a revision that actually loaded.
  showRevision(revision, candidate, failures) {
    const defaultRevisionId = this.state.blob["default_revision_id"];
    this.remember(candidate);
    const staleRevision = failures.length ? {failures, landing: candidate.from} : null;
    // Only a CHANGE discards the session. Arriving at a link that names one is deliberate -- the
    // session is resolved under the revision arrived at, and the reader is told when it is not there.
    const changing = !!this.state.revision && this.state.revision.id !== revision.id;
    this.setState({revision, staleRevision});
    this.syncRevisionQuery(revision.id, defaultRevisionId, changing ? this.pathAfterRevisionChange() : null);
  }

  // A session belongs to one revision -- New carries the definitions across and leaves the sessions
  // behind -- so a session named in the path is stale the moment the revision changes. Dropped here
  // rather than left for the shell, which would otherwise re-select a session of the revision just
  // left, or alert about one that was never in this one.
  pathAfterRevisionChange() {
    const {orgId, blobId, section, sectionId} = this.props.match.params;
    if ( "playground" === section ) {
      // `/playground/<definition>/<session>` -- the definition is looked up by alias and usually
      // survives, a session never does.
      return sectionId ? `/${orgId}/${blobId}/playground/${sectionId}` : `/${orgId}/${blobId}/playground`;
    }
    if ( "debugger" === section ) {
      // `/debugger/<session>/<execution>` -- the execution belongs to the session, so both go.
      return `/${orgId}/${blobId}/debugger`;
    }
    if ( "workflow" === section ) {
      // `/workflow/<definition>` -- a definition the new revision does not carry makes the editor
      // push a path of its own with no query, which drops `?revision=` and bounces the reader off
      // the revision they just chose. Dropping it here lands on that revision's first definition.
      return `/${orgId}/${blobId}/workflow`;
    }
    return this.props.match.path;
  }

  remember(candidate) {
    const remember = memoryOnLoad(candidate, this.state.blob["default_revision_id"]);
    applyRevisionMemory(this.scope(), {revisionId: candidate.id, remember});
  }

  requestRevision(revisionId) {
    const request = {id: revisionId, answer: this.props.app.api.revisionsIdGet(revisionId)};
    this.revisionRequest = request;
    return request;
  }

  // Whether `request` is still the one the page is waiting on; if so, it no longer is. A page the reader
  // has left wants no answer: applying one would save a choice and replace the URL they are on now.
  settle(request) {
    if ( this.unmounted || request !== this.revisionRequest ) {
      return false;
    }
    this.revisionRequest = null;
    return true;
  }

  // What a 2xx that carried no usable revision says: another blob's revision is an answer about the id,
  // a body carrying no revision at all is not an answer about anything and must forget nothing.
  unusableAnswer(response) {
    return response.data.revision ? "gone" : "transient";
  }

  // The response's revision, unless it is missing or belongs to another blob.
  loadedRevision(response) {
    const revision = response.data.revision;
    return revision && this.revisionBelongsToBlob(revision) ? revision : null;
  }

  // The list the selector offers. `onRevisionsNeeded` fires on every menu open by design, so "once" is
  // decided here, on an instance field a setState would not have landed in time to set. A failed fetch
  // is not an empty list, so the next open asks again rather than leaving a menu with no way to retry.
  // A new revision becomes the blob's default, so `selected` is what clears the remembered key rather
  // than pinning an id that is already the default.
  showCreatedRevision = (revisionId) => {
    this.changeRevision([{id: revisionId, from: "selected"}]);
  }

  // The selector's list is fetched at most once per page load, so an operation on the Revisions tab
  // would leave its menu stale until a reload. Dropping both makes the next open refetch.
  invalidateRevisions = () => {
    this.revisionsRequested = false;
    this.setState({revisions: null});
  }

  loadRevisions = () => {
    if ( this.revisionsRequested || !this.state.blob ) {
      return;
    }
    this.revisionsRequested = true;

    const orgId = this.props.match.params.orgId;
    const blobId = this.props.match.params.blobId;
    this.props.app.api.blobsIdRevisionsGet(orgId, blobId)
      .then(response => {
        if ( this.unmounted ) return;
        const revisions = response.data.revisions || [];
        this.setState({revisions});
        this.loadRevisionAuthors(revisions);
      }, error => {
        this.revisionsRequested = false;
      });
  }

  // A row names its author and the listing carries only `user_id`. `usersIdGet` memoises by id, so a
  // shared author costs one request, and an author that cannot be fetched is left out of its row
  // rather than printed as a raw uuid.
  loadRevisionAuthors(revisions) {
    const userIds = [...new Set(revisions.map(revision => revision.user_id).filter(Boolean))];
    for ( const userId of userIds ) {
      this.props.app.api.usersIdGet(userId)
        .then(response => {
          const user = response.data.user;
          if ( !user || this.unmounted ) return;
          this.setState(state => ({users: {...state.users, [userId]: user}}));
        }, error => {});
    }
  }

  // The row is only a label: the door fetches the revision. Choosing the one on screen switches nothing,
  // but it is still a choice, and choosing the default is what clears the saved one.
  handleRevisionSelect = (revision) => {
    if ( !revision || !revision.id ) {
      return;
    }
    const selected = {id: revision.id, from: "selected"};
    if ( this.state.revision && revision.id === this.state.revision.id ) {
      this.remember(selected);
      return;
    }
    this.changeRevision([selected]);
  }

  // ConfirmModal calls this with no arguments, and closes the dialog straight after -- which is why
  // the answer is recorded here rather than read off whatever happens to be in flight by then.
  confirmRevisionChange = () => {
    const pending = this.state.pendingSwitch;
    if ( pending ) {
      this.switchConfirmed = true;
      this.tryRevisions(pending.candidates, pending.failures);
    }
  }

  // Both answers close the dialog. A decline puts the bar back on the revision on screen, or a reload
  // would honour the link the reader has just declined; a confirm writes the bar from its own switch.
  closeRevisionSwitchConfirm = () => {
    const confirmed = this.switchConfirmed;
    this.switchConfirmed = false;
    this.setState({pendingSwitch: null});
    if ( !confirmed && this.state.revision ) {
      this.syncRevisionQuery(this.state.revision.id, this.state.blob["default_revision_id"]);
    }
  }

  // The only thing left that reads the old path form, which nothing produces any more: a new one must
  // never be generated. The id is RETURNED as well as replaced, because this runs from loadBlob's
  // setState callback and would read its own replace a render too late.
  translateLegacyRevisionPath() {
    const {orgId, blobId, section, sectionId} = this.props.match.params;
    if ( "revisions" !== section || !sectionId ) {
      return null;
    }
    const query = new URLSearchParams({revision: sectionId}).toString();
    this.props.history.replace(`/${orgId}/${blobId}?${query}`);
    return sectionId;
  }

  // A query parameter is a claim from outside, validated exactly like a stored one.
  queryRevisionId() {
    return new URLSearchParams(this.props.match.search || "").get("revision");
  }

  // `blob_id` is in the revision contract, but a response that omits it cannot be checked — absent
  // is not the same as wrong.
  revisionBelongsToBlob(revision) {
    const blobId = this.state.blob && this.state.blob.id;
    return !revision.blob_id || !blobId || revision.blob_id === blobId;
  }

  // A correction to the current location, so it leaves no Back step -- and none at all while a
  // default-tab redirect is pending, whose push (issued from render, before this runs) this would
  // otherwise undo. docs/specs/2026-09-18-revision-selection.md#revision-on-any-link
  syncRevisionQuery(revisionId, defaultRevisionId, path) {
    if ( this.pendingDefaultTab() ) {
      return;
    }
    const target = path || this.props.match.path;
    const search = this.revisionSearch(revisionId, defaultRevisionId);
    const searchChanged = search !== (this.props.match.search || "");
    if ( !searchChanged && target === this.props.match.path ) {
      return;
    }
    // Only a changed search is an echo worth marking: componentDidUpdate reconciles on the search and
    // ignores the path, so marking one the page did not change would leave a token nothing consumes.
    if ( searchChanged ) {
      this.barWrite = search;
    }
    this.props.history.replace(`${target}${search}`);
  }

  // The current query with `revision` set to the revision given, dropped when it is the default. The
  // rest of the query string survives.
  revisionSearch(revisionId, defaultRevisionId) {
    const params = new URLSearchParams(this.props.match.search || "");
    if ( revisionId && revisionId !== defaultRevisionId ) {
      params.set("revision", revisionId);
    } else {
      params.delete("revision");
    }
    const query = params.toString();
    return query ? `?${query}` : "";
  }

  // What every link this page builds carries, so a tab click keeps the revision a copied URL shares.
  shownRevisionSearch() {
    const blob = this.state.blob;
    const revision = this.state.revision;
    return this.revisionSearch(revision && revision.id, blob && blob["default_revision_id"]);
  }

  // The tab a workflow blob's owner has pointed the section-less URL at, or null. Read by render,
  // which performs the redirect, and by the bar's own write, which must not rewrite a URL that
  // redirect is about to replace.
  pendingDefaultTab() {
    if ( !this.state.org || !this.state.blob || "blobhub.compute.workflow" !== this.state.blob.type ) {
      return null;
    }
    if ( "default" !== this.activeSection() ) {
      return null;
    }
    const preferredTab = Preferences.get("blob:workflow.defaultTab", this.scope());
    const validTabs = ["workflow", "debugger", "playground", "revisions", "settings"];
    return (preferredTab && preferredTab !== "workflow" && validTabs.includes(preferredTab)) ? preferredTab : null;
  }

  /**
   * Tab orchestration
   */

  // The section the page is on, straight from the route -- the one place that reads it, so there is
  // one place a bucket could ever be added. docs/specs/2026-09-18-revision-selection.md#what-the-path-loses
  activeSection() {
    return this.props.match.params.section || "default";
  }

  resolveShell() {
    if (!this.state.blob) return null;
    return SHELLS[this.state.blob.type] || null;
  }

  buildDescriptors() {
    const Shell = this.resolveShell();
    if (!Shell || !Shell.getTabs) return [];

    const ctx = {
      blob: this.state.blob,
      revision: this.state.revision,
      app: this.props.app,
    };
    const descriptors = Shell.getTabs(ctx).slice();

    descriptors.push({ key: "revisions", title: "Revisions", icon: GoHistory });

    if (Shell.settingsDisabled) {
      descriptors.push({ key: "settings", title: "Settings", icon: GoGear, disabled: true });
    } else if (this.props.app.api.canAccessBlobSettings(this.state.blob)) {
      descriptors.push({ key: "settings", title: "Settings", icon: GoGear });
    }

    // Each descriptor carries the route its tab links to; BlobTabs renders it as a real href.
    return descriptors.map(d => ({ ...d, to: this.routeForDescriptor(d) }));
  }

  // BlobTabs' tabs are links and navigate on their own. Arrow-key traversal is the one activation
  // path that goes through react-bootstrap instead of a link, so the tab it reaches comes back here,
  // where the history lives, and takes the same route the tab's own href points at.
  navigateToDescriptor = (descriptor) => {
    if (descriptor.to) Navigation.push(this.props.history, descriptor.to);
  };

  defaultDescriptorKey(descriptors) {
    const def = descriptors.find(d => d.isDefault);
    return def ? def.key : (descriptors[0] && descriptors[0].key);
  }

  resolveActiveKey(descriptors, section) {
    if (section === "default") {
      return this.defaultDescriptorKey(descriptors);
    }
    const found = descriptors.find(d => d.key === section);
    return found ? found.key : this.defaultDescriptorKey(descriptors);
  }

  routeForDescriptor(descriptor) {
    const orgId = this.props.match.params.orgId;
    const blobId = this.props.match.params.blobId;
    // The "default" key is a sentinel for the section-less URL — used by graph and
    // dataset shells. Other shells (workflow, scheduler) keep their tab key as the
    // URL section even when the tab is marked isDefault, so clicking the tab does not
    // hit the section-less path and trigger the workflow defaultTab redirect.
    const search = this.shownRevisionSearch();
    if (descriptor.key === "default") {
      return `/${orgId}/${blobId}${search}`;
    }
    return `/${orgId}/${blobId}/${descriptor.key}${search}`;
  }

  /**
   * Rendering helpers
   */

  scope() {
    if (!this.state.org || !this.state.blob) return {};
    return { orgId: this.state.org.id, blobId: this.state.blob.id };
  }

  collapsed() {
    if (!this.state.org || !this.state.blob) return false;
    return !!Preferences.get("app.blobHeaderCollapsed", this.scope());
  }

  matchKey(activeKey, key) {
    return activeKey === key;
  }

  paneClass(activeKey, key) {
    return this.matchKey(activeKey, key) ? "" : "hidden";
  }

  // A 403 cannot tell the page whether the revision was deleted, so it offers both readings.
  renderStaleRevisionSentence({id, reason, kept}) {
    const code = <code>{id}</code>;
    if ( "forbidden" === reason ) {
      return <>
        Revision {code} is not available to you. It may belong to another blob, or your access to it may
        have changed.
      </>;
    }
    if ( "transient" === reason && kept ) {
      return <>
        Revision {code} could not be loaded. It is still remembered, and will be tried again the next time
        this blob opens.
      </>;
    }
    if ( "transient" === reason ) {
      return <>Revision {code} could not be loaded.</>;
    }
    return <>Revision {code} is no longer available.</>;
  }

  renderStaleRevisionNotice() {
    const stale = this.state.staleRevision;
    if ( !stale ) {
      return null;
    }
    const transient = stale.failures.some(failure => "transient" === failure.reason);
    return (
      <Container className="blob-header-row content-row blob-revision-notice">
        <Alert variant={transient ? "warning" : "info"} dismissible
               onClose={() => this.setState({staleRevision: null})}>
          {stale.failures.map(failure =>
            <div key={failure.id}>{this.renderStaleRevisionSentence(failure)}</div>
          )}
          {stale.landing && <div>{STALE_REVISION_LANDINGS[stale.landing]}</div>}
        </Alert>
      </Container>
    );
  }

  // Asked before the switch, because what it costs is gone by the time it has happened, and only when
  // a surface says it is holding something. ConfirmModal's contract comes with it: `danger` belongs on
  // the trigger, never here. docs/specs/2026-09-18-revision-selection.md#switching-remounts-and-the-mechanism-matters
  renderRevisionSwitchConfirm() {
    const pending = this.state.pendingSwitch;
    return (
      <ConfirmModal
        show={!!pending}
        onHide={this.closeRevisionSwitchConfirm}
        onConfirm={this.confirmRevisionChange}
        title="Switch Revision"
        confirmLabel="Switch"
        body={
          <>
            {pending && pending.failures.map(failure =>
              <p key={failure.id}>{this.renderStaleRevisionSentence(failure)}</p>
            )}
            Are you sure you want to switch to revision <strong>{pending ? pending.candidates[0].id : ""}</strong>?
            Unsaved changes in <strong>{pending ? this.surfaceList(pending.surfaces) : ""}</strong> will
            be discarded.
          </>
        }
      />
    );
  }

  // Four surfaces can answer on a workflow blob -- the editor, the playground and both markdown panes
  // -- so the list carries commas as well as an "and".
  surfaceList(surfaces) {
    if ( surfaces.length < 2 ) {
      return surfaces.join("");
    }
    return `${surfaces.slice(0, -1).join(", ")} and ${surfaces[surfaces.length - 1]}`;
  }

  renderCollapsedExtras(descriptors, activeKey) {
    const orgId = this.props.match.params.orgId;
    const orgLabel = Utils.displayName(this.state.org, orgId);
    const blobLabel = Utils.displayName(this.state.blob);
    return (
      <>
        <Nav className="blob-header-extras-id">
          <Nav.Link as={Link} to={`/${orgId}`} className="blob-header-extras-link">
            <GoRepo className="blob-header-extras-icon"/>
            {orgLabel}
          </Nav.Link>
          <span className="blob-header-extras-sep">/</span>
          <Nav.Link
            as={Link}
            to={`/${orgId}/${this.props.match.params.blobId}${this.shownRevisionSearch()}`}
            className="blob-header-extras-link blob-header-extras-link-strong"
          >
            {blobLabel}
          </Nav.Link>
        </Nav>
        <BlobTabs
          descriptors={descriptors}
          activeKey={activeKey}
          onKeyboardTraverse={this.navigateToDescriptor}
          compact
        />
        <BlobHeaderToggle scope={this.scope()} compact/>
      </>
    );
  }

  render() {
    if ( this.state.notFound ) {
      return (
        <>
          <Container className="blob-header-row header-message-row">
            <Alert variant="warning">Requested blob has not been found.</Alert>
          </Container>
        </>
      );
    }

    if ( null == this.state.blob ) {
      return (
        <>
          <Container className="blob-header-row header-message-row">
            <Alert variant="primary">Loading blob...</Alert>
          </Container>
        </>
      );
    } else {
      if ( this.props.app.api.hasBlobReachedTerminalSuccessStatus(this.state.blob) ) {
        // Just moving on.
      } else if ( this.props.app.api.isBlobInUpdatingStatus(this.state.blob) ) {
        // Just moving on.
      } else if ( this.props.app.api.hasBlobReachedTerminalFailureStatus(this.state.blob) ) {
        return (
          <>
            <Container className="blob-header-row header-message-row">
              <Alert variant="warning">Failed to create the blob. Please try again.</Alert>
            </Container>
          </>
        );
      } else {
        return (
          <>
            <Container className="blob-header-row header-message-row">
              <Alert variant="info" className="mb-0">The blob is under construction. Please wait...</Alert>
            </Container>
          </>
        );
      }
    }

    const section = this.activeSection();
    const sectionId = this.props.match.params.sectionId;

    // Apply default tab preference for workflow blobs. It carries the revision like every other link
    // the page builds: the bar's own write stands down while this is pending, so it is the only thing
    // that can keep the revision on the URL the redirect lands on.
    const preferredTab = this.pendingDefaultTab();
    if (preferredTab) {
      const orgId = this.props.match.params.orgId;
      const blobId = this.props.match.params.blobId;
      Navigation.push(this.props.history,
        `/${orgId}/${blobId}/${preferredTab}${this.shownRevisionSearch()}`);
      return null;
    }

    const descriptors = this.buildDescriptors();
    const activeKey = this.resolveActiveKey(descriptors, section);
    const collapsed = this.collapsed();
    const type = this.state.blob.type;
    const Shell = this.resolveShell();

    return (
      <>
        <ShortcutsModal
          show={this.state.shortcutsShow}
          onHide={() => this.setState({ shortcutsShow: false })}
          blob={this.state.blob}
        />

        {this.renderRevisionSwitchConfirm()}

        {!collapsed &&
          <BlobHeader
            app={this.props.app}
            org={this.state.org}
            blob={this.state.blob}
            revision={this.state.revision}
            revisions={this.state.revisions}
            users={this.state.users}
            onRevisionSelect={this.handleRevisionSelect}
            onRevisionsNeeded={this.loadRevisions}
            match={this.props.match}
            scope={this.scope()}
          />
        }

        {!collapsed &&
          <BlobTabs
            descriptors={descriptors}
            activeKey={activeKey}
            onKeyboardTraverse={this.navigateToDescriptor}
          />
        }

        {collapsed &&
          <HeaderSlotPortal targetId="blob-extras">
            {this.renderCollapsedExtras(descriptors, activeKey)}
          </HeaderSlotPortal>
        }

        {this.renderStaleRevisionNotice()}

        {/* The key is the mechanism, and it names the revision rather than the route: these surfaces
            capture what they are given at mount, so a change of revision re-renders them and nothing
            else. docs/specs/2026-09-18-revision-selection.md#switching-remounts-and-the-mechanism-matters */}
        {this.state.revision && Shell &&
          <>
            {Shell.rendersOwnVisibility ?
              <Shell
                key={this.state.revision.id}
                app={this.props.app}
                org={this.state.org}
                blob={this.state.blob}
                revision={this.state.revision}
                match={this.props.match}
                history={this.props.history}
                location={this.props.location}
                headerCollapsed={collapsed}
                unsavedWork={this.unsavedWork}
              />
              :
              <div className={this.paneClass(activeKey, "default")}>
                <Shell
                  key={this.state.revision.id}
                  app={this.props.app}
                  org={this.state.org}
                  blob={this.state.blob}
                  revision={this.state.revision}
                  section={section}
                  location={this.props.location}
                  unsavedWork={this.unsavedWork}
                />
              </div>
            }

            {/* blobhub.graph.orientdb is the one type with a second top-level pane. */}
            {"blobhub.graph.orientdb" === type &&
              <div className={this.paneClass(activeKey, "publish")}>
                <GraphOrientdbPublish
                  key={this.state.revision.id}
                  app={this.props.app}
                  blob={this.state.blob}
                  revision={this.state.revision}
                  section={section}
                  sectionId={sectionId}
                  match={this.props.match}
                />
              </div>
            }
          </>
        }

        {!this.state.revision && ["default", "debugger", "playground", "schedules", "workflow"].includes(activeKey) &&
          <Container className="blob-header-row content-row">
            <Alert variant="primary">Loading revision...</Alert>
          </Container>
        }

        <div className={this.paneClass(activeKey, "revisions")}>
          <BlobRevisions
            app={this.props.app}
            blob={this.state.blob}
            revision={this.state.revision}
            match={this.props.match}
            blobInterface={this.state}
            onRevisionsChanged={this.invalidateRevisions}
            onRevisionCreated={this.showCreatedRevision}
          />
        </div>

        <div className={this.paneClass(activeKey, "settings")}>
          {(this.state.blob && this.props.app.api.canAccessBlobSettings(this.state.blob)) &&
            <BlobSettings
              app={this.props.app}
              org={this.state.org}
              blob={this.state.blob}
              revision={this.state.revision}
              match={this.props.match}
              history={this.props.history}
              blobInterface={this.state}
            />
          }
        </div>

        {this.state.revision &&
          <>
            {/* Keyed for the same reason as the shell: md.js loads its content in componentDidMount
                and has no componentDidUpdate, so without this the previous revision's readme stays
                on screen after a switch. */}
            <div className={["default", "workflow"].includes(activeKey) ? "" : "hidden"}>
              <BlobMarkdown
                key={this.state.revision.id}
                app={this.props.app}
                blob={this.state.blob}
                revision={this.state.revision}
                match={this.props.match}
                unsavedWork={this.unsavedWork}
                metadata="readme"
                title="Readme"
                createPrompt="Describe this blob's contents and how to integrate with it."
              />
            </div>

            <div className={["default", "workflow"].includes(activeKey) ? "" : "hidden"}>
              <BlobMarkdown
                key={this.state.revision.id}
                app={this.props.app}
                blob={this.state.blob}
                revision={this.state.revision}
                match={this.props.match}
                unsavedWork={this.unsavedWork}
                metadata="license"
                title="License"
                createPrompt="Tell adopters how they may use and attribute this blob."
              />
            </div>
          </>
        }
      </>
    );
  }
}
